import { ProviderLogo } from "@/components/ProviderLogo";
import { InlineError } from "@/components/QueryState";
import { SlackIcon } from "@/components/SlackIcon";
import { TagInput } from "@/components/TagInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, type SsoApp, type SsoSettings, type SsoUpstream } from "@/lib/api";
import {
  accountPhrase,
  choiceOf,
  domainError,
  emailError,
  policySummary,
  providerList,
  UPSTREAM_LABELS,
  type AccessChoice,
} from "@/lib/sso";
import { radioGroupKeyDown } from "@/lib/radio-keys";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, ChevronRight, Globe, Loader2, UserCheck, Users } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

// Who can open the apps this Boo built. Each app registers itself for sign-in
// when the Boo builds it; this section lists them, says in plain words who gets
// in, and lets the Boo's admins change it. Hidden until the Boo has an app.
export function AppSignIn({ containerId }: { containerId: string }) {
  const settingsQuery = useQuery({
    queryKey: ["sso-settings", containerId],
    queryFn: () => api.sso.settings(containerId),
  });
  const enabled = settingsQuery.data?.enabled === true;
  const appsQuery = useQuery({
    queryKey: ["sso-apps", containerId],
    queryFn: () => api.sso.apps(containerId),
    enabled,
  });
  const [editing, setEditing] = useState<SsoApp | null>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);

  const settings = settingsQuery.data?.data;
  const apps = appsQuery.data?.data ?? [];
  if (!enabled || !settings) return null;
  if (appsQuery.isError) {
    return (
      <section className="space-y-2">
        <h2 className="text-base font-semibold">Who can open this Boo's apps</h2>
        <InlineError error={appsQuery.error} />
      </section>
    );
  }
  if (!apps.length) return null;

  const canEdit = appsQuery.data?._permissions.canEdit === true;
  const providers = settings.enabledUpstreams;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-end justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-balance">Who can open this Boo's apps</h2>
          <p className="text-sm text-muted-foreground text-pretty">
            {providers.length ? (
              <>
                People sign in with{" "}
                <span className="inline-flex items-center gap-1.5 align-[-0.15em] text-foreground">
                  {providers.map((u) => (
                    <ProviderLogo key={u} upstream={u} className="size-3.5" />
                  ))}
                  {providerList(providers)}
                </span>{" "}
                before an app opens.
              </>
            ) : (
              "No sign-in provider is on, so nobody can open these apps yet."
            )}
          </p>
        </div>
        {canEdit && settings.availableUpstreams.length > 1 && (
          <Button size="sm" variant="ghost" className="shrink-0" onClick={() => setOptionsOpen(true)}>
            Sign-in options
          </Button>
        )}
      </div>

      <ul className="overflow-hidden rounded-xl border divide-y">
        {apps.map((app) => (
          <li key={app.clientId}>
            <AppRow app={app} canEdit={canEdit} onOpen={() => setEditing(app)} />
          </li>
        ))}
      </ul>

      {!canEdit && (
        <p className="text-xs text-muted-foreground">Only this Boo's admins can change who gets in.</p>
      )}

      {editing && (
        <AccessDialog
          app={editing}
          containerId={containerId}
          settings={settings}
          onClose={() => setEditing(null)}
        />
      )}
      {optionsOpen && (
        <SignInOptionsDialog containerId={containerId} settings={settings} onClose={() => setOptionsOpen(false)} />
      )}
    </section>
  );
}

function hostOf(app: SsoApp): string | null {
  try {
    return new URL(app.redirectUris[0] ?? "").host;
  } catch {
    return null;
  }
}

function AccessIcon({ choice, className }: { choice: AccessChoice; className?: string }) {
  if (choice === "org_domain") return <Building2 className={className} />;
  if (choice === "allowlist") return <UserCheck className={className} />;
  if (choice === "roster") return <SlackIcon className={className} />;
  if (choice === "open") return <Globe className={className} />;
  return <Users className={className} />; // members
}

function AppRow({ app, canEdit, onOpen }: { app: SsoApp; canEdit: boolean; onOpen: () => void }) {
  const choice = choiceOf(app.policy);
  const host = hostOf(app);
  const body = (
    <>
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-semibold uppercase text-muted-foreground">
        {(app.name || app.appId).slice(0, 1)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{app.name || app.appId}</div>
        {host && <div className="truncate text-xs text-muted-foreground">{host}</div>}
      </div>
      <div
        className={cn(
          "flex min-w-0 max-w-[45%] items-center gap-1.5 text-sm",
          choice === "open" ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground",
        )}
      >
        <AccessIcon choice={choice} className="size-4 shrink-0" />
        <span className="truncate">{policySummary(app.policy)}</span>
      </div>
      {canEdit && <ChevronRight className="size-4 shrink-0 text-muted-foreground/70" />}
    </>
  );
  return canEdit ? (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
    >
      {body}
    </button>
  ) : (
    <div className="flex items-center gap-3 px-4 py-3">{body}</div>
  );
}

// ── Who can open one app ────────────────────────────────────────────────────

function ChoiceCard({
  value,
  selected,
  onSelect,
  icon,
  title,
  description,
  children,
}: {
  value: AccessChoice;
  selected: boolean;
  onSelect: (v: AccessChoice) => void;
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border transition-[border-color,background-color,box-shadow]",
        selected ? "border-foreground/30 bg-muted/40 shadow-sm" : "hover:bg-muted/30",
      )}
    >
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        tabIndex={selected ? 0 : -1}
        onClick={() => onSelect(value)}
        className="flex w-full items-start gap-3 p-3 text-left focus-visible:outline-none"
      >
        <span
          className={cn(
            "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors",
            selected ? "border-foreground" : "border-muted-foreground/40",
          )}
        >
          <span className={cn("size-2 rounded-full bg-foreground transition-transform", selected ? "scale-100" : "scale-0")} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-sm font-medium">
            {icon}
            {title}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground text-pretty">{description}</span>
        </span>
      </button>
      {selected && children && <div className="px-3 pb-3 pl-10">{children}</div>}
    </div>
  );
}

function AccessDialog({
  app,
  containerId,
  settings,
  onClose,
}: {
  app: SsoApp;
  containerId: string;
  settings: SsoSettings;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const initial = app.policy;
  const ownerDomain = settings.ownerDomain;
  const slackConnected = settings.detected.some((d) => d.upstream === "slack");
  const enabledUpstreams = settings.enabledUpstreams;

  const [choice, setChoice] = useState<AccessChoice>(choiceOf(initial));
  const [domains, setDomains] = useState<string[]>(
    initial.source === "custom" && initial.mode === "org_domain" ? initial.domains : ownerDomain ? [ownerDomain] : [],
  );
  const [emails, setEmails] = useState<string[]>(initial.source === "custom" ? initial.emails : []);
  // The providers this app accepts; all of the Boo's when the policy names none.
  const [upstreams, setUpstreams] = useState<SsoUpstream[]>(() => {
    const named = initial.upstreams.filter((u) => enabledUpstreams.includes(u));
    return named.length ? named : enabledUpstreams;
  });
  const [name, setName] = useState(app.name ?? "");
  const [description, setDescription] = useState(app.description ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const displayName = app.name || app.appId;

  const done = (message: string) => {
    queryClient.invalidateQueries({ queryKey: ["sso-apps", containerId] });
    queryClient.invalidateQueries({ queryKey: ["sso-audit", containerId] });
    toast.success(message);
    onClose();
  };
  const save = useMutation({
    // Who can open it first: it has the stricter checks, so a refusal there
    // leaves the name and description untouched too.
    mutationFn: async () => {
      const mode = choice;
      const policy = await api.sso.savePolicy(containerId, app.appId, {
        mode,
        domains: mode === "org_domain" ? domains : [],
        emails: mode === "allowlist" ? emails : [],
        // Every provider the Boo has on = no restriction, so turning one on later applies here too.
        upstreams: upstreams.length === enabledUpstreams.length ? [] : upstreams,
      });
      const nextName = name.trim() || null;
      const nextDescription = description.trim() || null;
      if (nextName !== (app.name ?? null) || nextDescription !== (app.description ?? null)) {
        await api.sso.updateApp(containerId, app.appId, { name: nextName, description: nextDescription });
      }
      return policy;
    },
    onSuccess: () => done(`Saved ${name.trim() || displayName}`),
  });

  const onSave = () => {
    if (name.trim().length > 80) return setProblem("The name can be up to 80 characters.");
    if (description.trim().length > 300) return setProblem("The description can be up to 300 characters.");
    if (choice === "org_domain" && !domains.length) return setProblem("Add at least one domain.");
    if (choice === "allowlist" && !emails.length) return setProblem("Add at least one email address.");
    if (!upstreams.length) return setProblem("Keep at least one way to sign in.");
    setProblem(null);
    save.mutate();
  };

  const select = (c: AccessChoice) => {
    setProblem(null);
    setChoice(c);
  };
  const anyone = enabledUpstreams.length ? `anyone with ${accountPhrase(enabledUpstreams)}` : "anyone";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 p-4">
          <DialogTitle>{displayName}</DialogTitle>
          <DialogDescription>Changes apply the next time someone signs in.</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={`app-name-${app.appId}`} className="text-xs">
                Name on the sign-in page
              </Label>
              <Input
                id={`app-name-${app.appId}`}
                value={name}
                maxLength={80}
                placeholder={app.appId}
                onChange={(e) => {
                  setProblem(null);
                  setName(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`app-description-${app.appId}`} className="text-xs">
                Description
              </Label>
              <Textarea
                id={`app-description-${app.appId}`}
                rows={2}
                value={description}
                maxLength={300}
                placeholder="What people can do in this app."
                onChange={(e) => {
                  setProblem(null);
                  setDescription(e.target.value);
                }}
                className="resize-none"
              />
            </div>
          </div>

          <div id={`who-can-open-${app.appId}`} className="text-xs font-medium -mb-1">
            Who can open it
          </div>

          <div
            role="radiogroup"
            aria-labelledby={`who-can-open-${app.appId}`}
            className="flex flex-col gap-2"
            onKeyDown={radioGroupKeyDown}
          >
            <ChoiceCard
              value="members"
              selected={choice === "members"}
              onSelect={select}
              icon={<Users className="size-4 text-muted-foreground" />}
              title="Your team"
              description="Only members of this Boo workspace."
            />
            <ChoiceCard
              value="org_domain"
              selected={choice === "org_domain"}
              onSelect={select}
              icon={<Building2 className="size-4 text-muted-foreground" />}
              title={ownerDomain ? `Anyone at ${ownerDomain}` : "Anyone at your company"}
              description="Everyone with an email at these domains, whether or not they're in this workspace."
            >
              <TagInput values={domains} onChange={setDomains} placeholder="acme.com" validate={domainError} />
            </ChoiceCard>
            <ChoiceCard
              value="allowlist"
              selected={choice === "allowlist"}
              onSelect={select}
              icon={<UserCheck className="size-4 text-muted-foreground" />}
              title="Specific people"
              description="Only the email addresses you add."
            >
              <TagInput values={emails} onChange={setEmails} placeholder="name@company.com" validate={emailError} />
            </ChoiceCard>
            {slackConnected && (
              <ChoiceCard
                value="roster"
                selected={choice === "roster"}
                onSelect={select}
                icon={<SlackIcon className="size-4" />}
                title="Your Slack workspace"
                description="Everyone in your connected Slack workspace, except guests."
              />
            )}
            <ChoiceCard
              value="open"
              selected={choice === "open"}
              onSelect={select}
              icon={<Globe className="size-4 text-amber-600 dark:text-amber-400" />}
              title="Anyone who signs in"
              description={`Public: ${anyone} can open it. The app still knows who they are.`}
            />
          </div>

          {enabledUpstreams.length > 1 && (
            <div className="space-y-2">
              <div className="text-xs font-medium">Sign in with</div>
              <div className="flex flex-wrap gap-4">
                {enabledUpstreams.map((u) => (
                  <label key={u} className="inline-flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4 rounded accent-foreground"
                      checked={upstreams.includes(u)}
                      onChange={(e) => {
                        setProblem(null);
                        const on = e.target.checked;
                        setUpstreams((prev) => (on ? [...new Set([...prev, u])] : prev.filter((x) => x !== u)));
                      }}
                    />
                    <ProviderLogo upstream={u} className="size-4" />
                    {UPSTREAM_LABELS[u]}
                  </label>
                ))}
              </div>
            </div>
          )}

          {problem && <p className="text-xs text-destructive">{problem}</p>}
          {save.isError && <InlineError error={save.error} />}
        </div>

        <DialogFooter className="mx-0 mb-0 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Which providers this Boo's apps offer (only when there's a choice) ──────

function SignInOptionsDialog({
  containerId,
  settings,
  onClose,
}: {
  containerId: string;
  settings: SsoSettings;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<SsoUpstream[]>(settings.enabledUpstreams);
  const refresh = (message: string) => {
    queryClient.invalidateQueries({ queryKey: ["sso-settings", containerId] });
    queryClient.invalidateQueries({ queryKey: ["sso-apps", containerId] });
    queryClient.invalidateQueries({ queryKey: ["sso-audit", containerId] });
    toast.success(message);
    onClose();
  };
  const save = useMutation({
    mutationFn: () => api.sso.saveSettings(containerId, { enabledUpstreams: enabled, extraDomains: settings.extraDomains }),
    onSuccess: () => refresh("Sign-in options saved"),
  });
  const reset = useMutation({
    mutationFn: () => api.sso.resetSettings(containerId),
    onSuccess: () => refresh("Back to the suggested sign-in options"),
  });
  const busy = save.isPending || reset.isPending;
  const reasonFor = (u: SsoUpstream) => settings.detected.find((d) => d.upstream === u)?.reason;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sign-in options</DialogTitle>
          <DialogDescription>How people can sign in to this Boo's apps.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col divide-y rounded-lg border">
          {settings.availableUpstreams.map((u) => {
            const reason = reasonFor(u);
            return (
              <label key={u} className="flex cursor-pointer items-center gap-3 px-3 py-2.5">
                <ProviderLogo upstream={u} className="size-5" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{UPSTREAM_LABELS[u]}</span>
                  {reason && <span className="block text-xs text-muted-foreground">Suggested: {reason.charAt(0).toLowerCase() + reason.slice(1)}</span>}
                </span>
                <Switch
                  checked={enabled.includes(u)}
                  disabled={busy}
                  onCheckedChange={(on) =>
                    setEnabled((prev) => (on ? [...new Set([...prev, u])] : prev.filter((x) => x !== u)))
                  }
                />
              </label>
            );
          })}
        </div>

        {!enabled.length && <p className="text-xs text-destructive">Keep at least one way to sign in.</p>}
        {save.isError && <InlineError error={save.error} />}
        {reset.isError && <InlineError error={reset.error} />}

        <DialogFooter className="sm:justify-between">
          {settings.source === "custom" ? (
            <Button variant="ghost" onClick={() => reset.mutate()} disabled={busy}>
              Use suggested
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={busy || !enabled.length}>
              {save.isPending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
